import { useState, useRef, useCallback, useEffect } from 'react';
import { Eraser, RotateCcw, Check, X, ZoomIn, ZoomOut } from 'lucide-react';

interface SegmentEraserProps {
  imageDataUrl: string;
  onSave: (editedDataUrl: string) => void;
  onCancel: () => void;
}

/**
 * Canvas-based eraser tool for cleaning up garment segmentation masks.
 * User can paint over areas to erase them (make transparent).
 */
export function SegmentEraser({ imageDataUrl, onSave, onCancel }: SegmentEraserProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(30);
  const [history, setHistory] = useState<ImageData[]>([]);
  const [zoom, setZoom] = useState(1);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  // Load image onto canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      // Save initial state for undo
      setHistory([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  const getCanvasPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const erase = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();

    // If we have a last position, draw a line to smooth the stroke
    if (lastPosRef.current) {
      const { x: lx, y: ly } = lastPosRef.current;
      const dist = Math.hypot(x - lx, y - ly);
      const steps = Math.max(1, Math.floor(dist / (brushSize / 4)));
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const ix = lx + (x - lx) * t;
        const iy = ly + (y - ly) * t;
        ctx.moveTo(ix + brushSize / 2, iy);
        ctx.arc(ix, iy, brushSize / 2, 0, Math.PI * 2);
      }
    } else {
      ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
    }

    ctx.fill();
    ctx.restore();
    lastPosRef.current = { x, y };
  }, [brushSize]);

  const handleStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPosRef.current = null;
    const pos = getCanvasPos(e);
    erase(pos.x, pos.y);
  }, [getCanvasPos, erase]);

  const handleMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const pos = getCanvasPos(e);
    erase(pos.x, pos.y);
  }, [isDrawing, getCanvasPos, erase]);

  const handleEnd = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    lastPosRef.current = null;
    // Save state for undo
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) {
      setHistory(prev => [...prev, ctx.getImageData(0, 0, canvas.width, canvas.height)]);
    }
  }, [isDrawing]);

  const handleUndo = useCallback(() => {
    if (history.length <= 1) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const newHistory = history.slice(0, -1);
    const prevState = newHistory[newHistory.length - 1];
    ctx.putImageData(prevState, 0, 0);
    setHistory(newHistory);
  }, [history]);

  const handleSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSave(canvas.toDataURL('image/png'));
  }, [onSave]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900">
        <button onClick={onCancel} className="text-white p-2 rounded-lg hover:bg-gray-700">
          <X size={20} />
        </button>
        <div className="flex items-center gap-3">
          <Eraser size={16} className="text-gray-400" />
          <input
            type="range"
            min="5"
            max="80"
            value={brushSize}
            onChange={e => setBrushSize(Number(e.target.value))}
            className="w-24 accent-purple-500"
          />
          <span className="text-xs text-gray-400 w-8">{brushSize}px</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="text-white p-2 rounded-lg hover:bg-gray-700">
            <ZoomOut size={18} />
          </button>
          <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} className="text-white p-2 rounded-lg hover:bg-gray-700">
            <ZoomIn size={18} />
          </button>
          <button
            onClick={handleUndo}
            disabled={history.length <= 1}
            className="text-white p-2 rounded-lg hover:bg-gray-700 disabled:opacity-30"
          >
            <RotateCcw size={18} />
          </button>
          <button onClick={handleSave} className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-1.5 text-sm font-medium">
            <Check size={16} /> Done
          </button>
        </div>
      </div>

      {/* Hint */}
      <div className="text-center py-2 text-gray-400 text-xs">
        Paint over areas to erase them. Pinch or use buttons to zoom.
      </div>

      {/* Canvas area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto flex items-center justify-center"
        style={{ background: 'repeating-conic-gradient(#333 0% 25%, #444 0% 50%) 50% / 20px 20px' }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          className="max-w-full max-h-full"
          style={{
            cursor: 'crosshair',
            transform: `scale(${zoom})`,
            transformOrigin: 'center',
            imageRendering: zoom > 1.5 ? 'pixelated' : 'auto',
          }}
        />
      </div>
    </div>
  );
}
